import { useCallback, useEffect, useState } from 'react';
import { fetchConcessions, type LendwiseConcession } from '@/services/lendwiseConcessionService';
import { getLoanPricingItems, type LoanPricingItem } from '@/services/loanPricingService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useGateLocals', 'pricing-v2');

export interface GateLocalsOptions {
  /**
   * When `true`, the hook returns `overrideConcessions ?? []` and
   * `overridePricingItems ?? []` verbatim and performs no network work.
   * The hook is mode-agnostic; Product & Pricing normally calls it without
   * overrides so UI-owned pricing fields stay live across lock selection.
   */
  skipLiveFetches?: boolean;
  overrideConcessions?: LendwiseConcession[];
  overridePricingItems?: LoanPricingItem[];
}

export interface GateLocals {
  concessions: LendwiseConcession[];
  pricingItems: LoanPricingItem[];
  isLoading: boolean;
  /**
   * Re-fetch `lendwise_concessions` for the current loan. Used by the
   * Concessions & Cure modal after add/delete so the parent view updates
   * without a full page refetch. No-op only when the caller explicitly uses
   * `skipLiveFetches`.
   */
  refreshConcessions: () => Promise<void>;
  /** Re-fetch `loan_pricing_items` for the current loan. No-op only under `skipLiveFetches`. */
  refreshPricingItems: () => Promise<void>;
}

/**
 * Loads the two tables the workspace needs that are not already on the loan
 * row: `lendwise_concessions` and `loan_pricing_items`.
 *
 * Mode-agnostic. The caller decides whether to short-circuit by passing
 * `skipLiveFetches: true` along with overrides. Product & Pricing calls
 * without overrides in live, active delivery-lock, and historical inspection
 * modes so local pricing fields remain live.
 */
export function useGateLocals(
  loanId: string,
  encompassLoanId: string,
  options: GateLocalsOptions = {}
): GateLocals {
  const { skipLiveFetches = false, overrideConcessions, overridePricingItems } = options;
  const [concessions, setConcessions] = useState<LendwiseConcession[]>(overrideConcessions ?? []);
  const [pricingItems, setPricingItems] = useState<LoanPricingItem[]>(overridePricingItems ?? []);
  const [isLoading, setIsLoading] = useState(!skipLiveFetches);

  useEffect(() => {
    if (skipLiveFetches) {
      setConcessions(overrideConcessions ?? []);
      setPricingItems(overridePricingItems ?? []);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      fetchConcessions(loanId).catch((err) => {
        log.error('Failed to load concessions', { loanId }, err as Error);
        return [] as LendwiseConcession[];
      }),
      encompassLoanId
        ? getLoanPricingItems(encompassLoanId).catch((err) => {
            log.error('Failed to load pricing items', { encompassLoanId }, err as Error);
            return [] as LoanPricingItem[];
          })
        : Promise.resolve([] as LoanPricingItem[])
    ]).then(([c, p]) => {
      if (cancelled) {
        return;
      }
      setConcessions(c);
      setPricingItems(p);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loanId, encompassLoanId, skipLiveFetches, overrideConcessions, overridePricingItems]);

  const refreshConcessions = useCallback(async () => {
    if (skipLiveFetches) {
      return;
    }
    try {
      const next = await fetchConcessions(loanId);
      setConcessions(next);
    } catch (err) {
      log.error('Failed to refresh concessions', { loanId }, err as Error);
    }
  }, [loanId, skipLiveFetches]);

  const refreshPricingItems = useCallback(async () => {
    if (skipLiveFetches || !encompassLoanId) {
      return;
    }
    try {
      const next = await getLoanPricingItems(encompassLoanId);
      setPricingItems(next);
    } catch (err) {
      log.error('Failed to refresh pricing items', { encompassLoanId }, err as Error);
    }
  }, [encompassLoanId, skipLiveFetches]);

  return { concessions, pricingItems, isLoading, refreshConcessions, refreshPricingItems };
}
